import moment, { Moment } from 'moment';
import { EquipmentModel } from '../constants/equipmentConstants';
import { PARCELS_CONSTANTS } from '../constants/parcelsConstants';
import { WORKSITE_TASK_STATUS } from '../constants/worksiteConstants';
import { ClusterId } from '../slices/clusters';
import { Equipment, EquipmentId } from '../slices/equipments';
import { Parcel, ParcelId } from '../slices/parcels';
import { DeviceSessionId, EquipmentSession } from '../slices/sessions';
import { TaskType } from '../slices/taskTypes';
import { UserId } from '../slices/users';
import type { Worksite, WorksiteKey } from '../slices/worksites';
import { isPhytoTaskNotSeed } from './taskTypes';

type WorksiteEquipment = Pick<Equipment, 'id'> & {
  equipment_model?: EquipmentModel;
  equipmentModel?: { type?: EquipmentModel };
};

type EquipmentWorksite<E extends WorksiteEquipment = WorksiteEquipment> = Pick<Worksite, 'equipment_model'> & {
  equipment_instances: E[];
};

type DriverParcelSession = EquipmentSession['parcel_sessions'][number] & { user_id?: UserId };

type DriverSession = Omit<EquipmentSession, 'parcel_sessions'> & { parcel_sessions: DriverParcelSession[] };

type WorksiteParcelData = {
  id: ParcelId;
  parcel_name: string;
  parcel_area: number;
  from_date: string;
  to_date: string;
  motor_duration: number;
  distance: number;
  area: number;
  unit: number;
};

export function getParcelDataFromWorksite(worksite: Partial<Pick<Worksite, 'sessions'>>): WorksiteParcelData[] {
  const parcelsById: Record<ParcelId, WorksiteParcelData> = {};
  for (const equipmentSession of worksite.sessions || []) {
    for (const parcelSession of equipmentSession.parcel_sessions || []) {
      let parcel = parcelsById[parcelSession.parcel_id];
      if (!parcel) {
        parcel = {
          id: parcelSession.parcel_id,
          parcel_name: parcelSession.parcel_name,
          parcel_area: parcelSession.parcel_area,
          from_date: parcelSession.from_date,
          to_date: parcelSession.to_date,
          motor_duration: 0,
          distance: 0,
          area: 0,
          unit: 0,
        };
        parcelsById[parcelSession.parcel_id] = parcel;
      }

      parcel.from_date = moment(parcelSession.from_date).isBefore(moment(parcel.from_date))
        ? parcelSession.from_date
        : parcel.from_date;
      parcel.to_date = moment(parcelSession.to_date).isAfter(moment(parcel.to_date))
        ? parcelSession.to_date
        : parcel.to_date;
      parcel.motor_duration += parcelSession.motor_duration;
      parcel.distance += parcelSession.distance;
      parcel.area += parcelSession.area;
      parcel.unit += parcelSession.unit;
    }
  }
  return Object.values(parcelsById);
}

export function getEquipmentFromWorksite<E extends WorksiteEquipment>(
  worksite?: EquipmentWorksite<E> | null,
): E | undefined {
  if (!worksite) return;
  return worksite.equipment_instances.find(
    (e) => (e.equipment_model || e.equipmentModel?.type) === worksite.equipment_model,
  );
}

export function getWorksiteKey(
  worksite: EquipmentWorksite & Pick<Worksite, 'cluster_id' | 'device_session_id'>,
): WorksiteKey | 'error' {
  const equipment = getEquipmentFromWorksite(worksite);
  if (!equipment) return 'error';
  return `${equipment.id}-${worksite.cluster_id}-${worksite.device_session_id}`;
}

type WorksiteTrack = {
  properties?: {
    equipment_instance_id: EquipmentId;
    cluster_id: ClusterId;
    device_session_id: DeviceSessionId;
  };
};

export function getWorksiteKeyFromTrack(feature?: WorksiteTrack | null): WorksiteKey | 'error' {
  if (!feature?.properties) return 'error';
  return `${feature.properties.equipment_instance_id}-${feature.properties.cluster_id}-${feature.properties.device_session_id}`;
}

/** `undefined` if this worksite has two or zero drivers, and the driver’s id if it’s the only one */
function getUniqueDriverIdFromWorksite(worksite: { sessions: DriverSession[] }): UserId | undefined {
  const userIds = new Set(
    worksite.sessions
      .map((s) => s.parcel_sessions)
      .flat()
      .map((p) => p?.user_id)
      .filter(Boolean),
  );
  return userIds.size === 1 ? userIds.values().next().value : undefined;
}

type CompiledWorksitesData = {
  startDate: Moment | null;
  endDate: Moment | null;
  equipmentIds: EquipmentId[];
  driverIds: UserId[];
  clusterIds: ClusterId[];
};

type CompilableWorksite = EquipmentWorksite &
  Pick<Worksite, 'cluster_id' | 'from_date' | 'to_date'> & { sessions: DriverSession[] };

export function getCompiledWorksitesData(worksites: CompilableWorksite[]): CompiledWorksitesData {
  const result: CompiledWorksitesData = {
    startDate: null,
    endDate: null,
    equipmentIds: [],
    driverIds: [],
    clusterIds: [],
  };

  for (const worksite of worksites) {
    if (result.startDate) {
      result.startDate = moment.min(result.startDate, moment(worksite.from_date));
    } else {
      result.startDate = moment(worksite.from_date);
    }

    if (result.endDate) {
      result.endDate = moment.max(result.endDate, moment(worksite.to_date));
    } else {
      result.endDate = moment(worksite.to_date);
    }

    const equipmentId = getEquipmentFromWorksite(worksite)?.id;
    if (equipmentId) {
      result.equipmentIds = [...new Set(result.equipmentIds.concat([equipmentId]))];
    }

    const driverId = getUniqueDriverIdFromWorksite(worksite);
    if (driverId) {
      result.driverIds = [...new Set(result.driverIds.concat(driverId))];
    }

    if (worksite.cluster_id) {
      result.clusterIds = [...new Set(result.clusterIds.concat(worksite.cluster_id))];
    }
  }

  return result;
}

export function getWorksiteParcelIdsSortedByDate(
  worksites: Partial<Pick<Worksite, 'sessions'>>[],
  ownerClusterId: ClusterId,
  clientIds: ClusterId[],
  allParcelsById: Record<ParcelId, Parcel>,
): ParcelId[] {
  const parcelsById: Record<ParcelId, WorksiteParcelData> = {};

  for (const worksite of worksites) {
    const parcels = getParcelDataFromWorksite(worksite).filter((p) =>
      filterWorksiteParcelsForTask(p.id, ownerClusterId, clientIds, allParcelsById),
    );
    for (const parcel of parcels) {
      const knownParcel = parcelsById[parcel.id];
      if (!knownParcel) {
        parcelsById[parcel.id] = parcel;
      } else if (moment(parcel.from_date).isBefore(moment(knownParcel.from_date))) {
        knownParcel.from_date = parcel.from_date;
      }
    }
  }

  return Object.entries(parcelsById)
    .sort(([, a], [, b]) => moment(a.from_date).diff(moment(b.from_date)))
    .map(([id]) => Number(id));
}

export function getParcelAreaFromWorksites(
  worksites: Partial<Pick<Worksite, 'sessions'>>[],
  parcelId: ParcelId,
): number {
  let area = 0;
  for (const worksite of worksites) {
    const parcels = getParcelDataFromWorksite(worksite);
    const parcel = parcels.find((p) => p.id === parcelId);
    if (parcel) {
      area += parcel.area || 0;
    }
  }
  return area;
}

export function filterWorksiteParcelsForTask(
  parcelId: ParcelId,
  ownerClusterId: ClusterId,
  clientIds: ClusterId[] = [],
  parcelsById: Record<ParcelId, Parcel> = {},
) {
  const parcel = parcelsById[parcelId];
  const excludeNonAnonFromOwner = !clientIds.includes(ownerClusterId);
  return (
    !parcel || !excludeNonAnonFromOwner || (parcel.current_tenant_cluster_id ?? parcel.cluster_id) !== ownerClusterId
  );
}

type CategorizedWorksite = Partial<Pick<Worksite, 'worked_parcel_with_tasks'>>;

/**
 * Returns a `parcelId -> parcel_category` lookup. Reads the category from the worksites' `worked_parcel_with_tasks`
 * (always available on the worksite payload) and falls back to the parcels store. This matters because a worksite's
 * parcels are not necessarily in the store yet when it gets selected (they are fetched afterwards).
 */
function getParcelCategoryGetter(
  worksites: CategorizedWorksite[] | null | undefined,
  parcelsById: Record<ParcelId, Parcel> = {},
) {
  const categoryById: Record<ParcelId, string> = {};
  for (const worksite of worksites || []) {
    for (const p of worksite.worked_parcel_with_tasks || []) {
      categoryById[p.parcel_id] = p.parcel_category;
    }
  }
  return (parcelId: ParcelId) => categoryById[parcelId] ?? parcelsById[parcelId]?.category;
}

/**
 * Parcels of a worksite that should be auto-selected for a task: eligible parcels minus farmhouse parcels on a phyto
 * (non-seed) task. Farmhouse parcels stay visible in the list but must not be checked automatically.
 */
export function getWorksiteParcelsToSelectForTask(
  worksite: Partial<Pick<Worksite, 'sessions'>> & CategorizedWorksite,
  ownerClusterId: ClusterId,
  clientIds: ClusterId[],
  parcelsById: Record<ParcelId, Parcel>,
  taskType?: TaskType,
): WorksiteParcelData[] {
  const isPhytoNotSeed = isPhytoTaskNotSeed(taskType);
  const getCategory = getParcelCategoryGetter([worksite], parcelsById);
  return getParcelDataFromWorksite(worksite).filter(
    (p) =>
      filterWorksiteParcelsForTask(p.id, ownerClusterId, clientIds, parcelsById) &&
      !(isPhytoNotSeed && getCategory(p.id) === PARCELS_CONSTANTS.CATEGORY.FARMHOUSE),
  );
}

/**
 * Removes farmhouse parcel IDs (coming from worksites) on a phyto (non-seed) task. Used when pre-selecting worksite
 * parcels into a work order.
 */
export function excludeFarmhouseWorksiteParcelIds(
  parcelIds: ParcelId[],
  worksites: CategorizedWorksite[] | null | undefined,
  parcelsById: Record<ParcelId, Parcel>,
  taskType?: TaskType,
): ParcelId[] {
  if (!isPhytoTaskNotSeed(taskType)) return parcelIds;
  const getCategory = getParcelCategoryGetter(worksites, parcelsById);
  return parcelIds.filter((id) => getCategory(id) !== PARCELS_CONSTANTS.CATEGORY.FARMHOUSE);
}

type WorksiteSelectionFilters = {
  clientIds: ClusterId[];
  showAnonWorksites: boolean;
  showUsedWorksites: boolean;
  showParcelWorksitesOnly: boolean;
};

export function isWorksiteShownInSelection(
  worksite: Pick<Worksite, 'cluster_id' | 'task_status' | 'worked_parcel_with_tasks'>,
  filters: WorksiteSelectionFilters,
) {
  return (
    (filters.showAnonWorksites || filters.clientIds.includes(worksite.cluster_id)) &&
    (filters.showUsedWorksites || worksite.task_status !== WORKSITE_TASK_STATUS.CONSIDERED) &&
    (!filters.showParcelWorksitesOnly ||
      (worksite.worked_parcel_with_tasks || []).some((p) => p.parcel_category !== PARCELS_CONSTANTS.CATEGORY.FARMHOUSE))
  );
}
